import React, { useEffect, useState } from 'react';
import { Button, ConfirmationModal, PageShell, Table } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch, fetchClients, deleteClientById } from 'data';
import { useTranslation } from 'react-i18next';

export const Clients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { clients, count, getClientsLoading, clientsError } = useSelector(
    (state: RootState) => state.clients,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(count / itemsPerPage);

  useEffect(() => {
    dispatch(fetchClients({ page: currentPage - 1, per_page: itemsPerPage }));
  }, [dispatch, currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleDeleteClient = async () => {
    if (selectedClientId) {
      try {
        await dispatch(deleteClientById(selectedClientId)).unwrap();
        setIsModalOpen(false);
        console.log(t('clients.toast.clientDeletedSuccessfully'));
      } catch (error) {
        console.log(t('clients.toast.failedToDeleteClient'));
      }
    }
  };

  const openDeleteModal = (clientId: number) => {
    setSelectedClientId(clientId);
    setIsModalOpen(true);
  };

  const getActions = (row: any) => [
    {
      label: t('clients.createInvoice'),
      onClick: () => navigate(`/invoices/create-new?clientId=${row.id}`),
    },
    {
      label: t('clients.edit'),
      onClick: () => navigate(`/clients/edit/${row.id}`),
    },
    {
      label: t('clients.delete'),
      onClick: () => openDeleteModal(row.id),
      className: 'text-red-600 hover:bg-red-100',
    },
  ];

  return (
    <>
      <PageShell
        title={t('clients.clients')}
        actions={
          <Button
            label={t('clients.addNewClient')}
            onClick={() => navigate('/clients/add-new')}
            fullWidth
          />
        }
      >

        {getClientsLoading && (
          <p className="text-center text-gray-500">
            {t('clients.loadingClients')}
          </p>
        )}
        {clientsError && (
          <p className="text-center text-red-500">{clientsError}</p>
        )}

        {!getClientsLoading && !clientsError && clients.length > 0 ? (
          <Table
            data={clients.map((client) => ({
              id: client.id,
              companyName: client.name,
              phoneNumber: client.phone || 'N/A',
              email: client.email || 'N/A',
              country: client.address?.Country || 'N/A',
              city: client.address?.City || 'N/A',
              contactPerson: client.contactPersonFirstName || 'N/A',
            }))}
            columns={[
              { key: 'companyName', label: t('clients.company') },
              { key: 'phoneNumber', label: t('clients.phone') },
              { key: 'email', label: t('clients.email') },
              { key: 'country', label: t('clients.country') },
              { key: 'city', label: t('clients.city') },
              { key: 'contactPerson', label: t('clients.contactPerson') },
            ]}
            searchField={true}
            searchFunctionality={true}
            actions={getActions}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            cardLayout={{
              title: 'companyName',
              meta: [
                'contactPerson',
                'email',
                'phoneNumber',
                'city',
                'country',
              ],
            }}
          />
        ) : (
          !getClientsLoading && (
            <p className="text-center text-gray-500">
              {t('clients.noClientsFound')}
            </p>
          )
        )}
      </PageShell>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeleteClient}
      />
    </>
  );
};
